import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import { forceChangePassword, clearError, logout } from '../../redux/slices/authSlice';
import { APP_VERSION } from '../../version';

const ChangePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Must contain uppercase, lowercase, number, and special character (@$!%*?&)'
    )
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const ForceChangePasswordPage = () => {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, user } = useSelector((state) => state.auth);

  // If user no longer needs to change password, redirect to home
  useEffect(() => {
    if (user && !user.must_change_password) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (values) => {
    const result = await dispatch(
      forceChangePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
    );
    if (result.meta.requestStatus === 'fulfilled') {
      navigate('/');
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/auth/login');
  };

  const inputSx = {
    mb: 2.5,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover fieldset': { borderColor: '#2C3E50' },
      '&.Mui-focused fieldset': { borderColor: '#2C3E50' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#2C3E50' },
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Panel - Branding */}
      <Box
        sx={{
          flex: '0 0 45%',
          bgcolor: '#2C3E50',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: 8,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            opacity: 0.04,
            backgroundImage:
              'radial-gradient(circle at 25% 25%, #fff 1px, transparent 1px), radial-gradient(circle at 75% 75%, #fff 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: { md: '3.5rem', lg: '4.5rem' },
              fontWeight: 700,
              color: '#fff',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Biztimize
          </Typography>
          <Typography
            sx={{
              fontSize: { md: '1rem', lg: '1.15rem' },
              color: 'rgba(255,255,255,0.6)',
              fontWeight: 400,
              maxWidth: 420,
            }}
          >
            Optimize your Business
          </Typography>
        </Box>
        <Typography
          sx={{
            position: 'absolute',
            bottom: 32,
            left: 0, right: 0,
            textAlign: 'center',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.3)',
          }}
        >
          &copy; {new Date().getFullYear()} Biztimize. All rights reserved. &nbsp;v{APP_VERSION}
        </Typography>
      </Box>

      {/* Right Panel - Change Password Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#EEF2F7',
          px: { xs: 3, sm: 6 },
          py: 4,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            bgcolor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            p: { xs: 3.5, sm: 4.5 },
          }}
        >
          {/* Icon */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#FFF3E0',
                mb: 1,
              }}
            >
              <LockReset sx={{ fontSize: 28, color: '#E65100' }} />
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: '1.4rem',
              fontWeight: 700,
              color: '#212529',
              textAlign: 'center',
              mb: 0.5,
            }}
          >
            Change Your Password
          </Typography>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6c757d',
              textAlign: 'center',
              mb: 3,
            }}
          >
            Your administrator has set an initial password for your account.
            Please create a new password to continue.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2.5, borderRadius: 1.5 }}
              onClose={() => dispatch(clearError())}
            >
              {typeof error === 'object'
                ? error.current_password || error.new_password || error.detail || JSON.stringify(error)
                : error}
            </Alert>
          )}

          <Formik
            initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
            validationSchema={ChangePasswordSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <Form>
                <TextField
                  fullWidth
                  name="currentPassword"
                  label="Current Password"
                  type={showCurrent ? 'text' : 'password'}
                  value={values.currentPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.currentPassword && Boolean(errors.currentPassword)}
                  helperText={touched.currentPassword && errors.currentPassword}
                  disabled={isLoading}
                  autoComplete="current-password"
                  autoFocus
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrent(!showCurrent)}
                          edge="end"
                          disabled={isLoading}
                          sx={{ color: '#6c757d' }}
                        >
                          {showCurrent ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  name="newPassword"
                  label="New Password"
                  type={showNew ? 'text' : 'password'}
                  value={values.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.newPassword && Boolean(errors.newPassword)}
                  helperText={touched.newPassword && errors.newPassword}
                  disabled={isLoading}
                  autoComplete="new-password"
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNew(!showNew)}
                          edge="end"
                          disabled={isLoading}
                          sx={{ color: '#6c757d' }}
                        >
                          {showNew ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="Confirm New Password"
                  type={showConfirm ? 'text' : 'password'}
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  disabled={isLoading}
                  autoComplete="new-password"
                  sx={{ ...inputSx, mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirm(!showConfirm)}
                          edge="end"
                          disabled={isLoading}
                          sx={{ color: '#6c757d' }}
                        >
                          {showConfirm ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password requirements hint */}
                <Box sx={{ mb: 3, px: 1 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.8 }}>
                    Password must contain at least 8 characters, one uppercase letter,
                    one lowercase letter, one number, and one special character (@$!%*?&).
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '28px',
                    bgcolor: '#2C3E50',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#1A242F',
                      boxShadow: '0 4px 12px rgba(44,62,80,0.3)',
                    },
                    '&:disabled': {
                      bgcolor: '#2C3E50',
                      opacity: 0.7,
                      color: '#fff',
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    'Set New Password'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                  <Typography
                    onClick={handleLogout}
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#3498db',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Sign out
                  </Typography>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Box>
  );
};

export default ForceChangePasswordPage;
